import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, GripVertical, Trash2 } from "lucide-react";
import "./index.css";

interface Todo {
	id: string;
	text: string;
	completed: boolean;
}

export function App() {
	const [todos, setTodos] = useState<Todo[]>([]);
	const [newTodo, setNewTodo] = useState("");
	const [draggedId, setDraggedId] = useState<string | null>(null);
	const [dragOverId, setDragOverId] = useState<string | null>(null);
	const [dropPosition, setDropPosition] = useState<"above" | "below" | null>(null);
	const dragCounter = useRef<Map<string, number>>(new Map());

	const addTodo = () => {
		const text = newTodo.trim();
		if (!text) return;
		setTodos((prev) => [
			...prev,
			{ id: crypto.randomUUID(), text, completed: false },
		]);
		setNewTodo("");
	};

	const toggleTodo = (id: string) => {
		setTodos((prev) =>
			prev.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t))
		);
	};

	const deleteTodo = (id: string) => {
		setTodos((prev) => prev.filter((t) => t.id !== id));
	};

	const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
		setDraggedId(id);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", id);
		// Make the drag image slightly transparent
		if (e.currentTarget instanceof HTMLElement) {
			e.currentTarget.style.opacity = "0.5";
		}
	}, []);

	const handleDragEnd = useCallback((e: React.DragEvent) => {
		if (e.currentTarget instanceof HTMLElement) {
			e.currentTarget.style.opacity = "1";
		}
		setDraggedId(null);
		setDragOverId(null);
		setDropPosition(null);
		dragCounter.current.clear();
	}, []);

	const handleDragEnter = useCallback(
		(e: React.DragEvent, id: string) => {
			e.preventDefault();
			if (id === draggedId) return;
			const count = (dragCounter.current.get(id) || 0) + 1;
			dragCounter.current.set(id, count);
			setDragOverId(id);
		},
		[draggedId]
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent, id: string) => {
			e.preventDefault();
			e.dataTransfer.dropEffect = "move";
			if (id === draggedId) return;

			const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
			const midY = rect.top + rect.height / 2;
			setDropPosition(e.clientY < midY ? "above" : "below");
		},
		[draggedId]
	);

	const handleDragLeave = useCallback(
		(e: React.DragEvent, id: string) => {
			e.preventDefault();
			const count = (dragCounter.current.get(id) || 0) - 1;
			dragCounter.current.set(id, count);
			if (count <= 0) {
				dragCounter.current.delete(id);
				if (dragOverId === id) {
					setDragOverId(null);
					setDropPosition(null);
				}
			}
		},
		[dragOverId]
	);

	const handleDrop = useCallback(
		(e: React.DragEvent, targetId: string) => {
			e.preventDefault();
			dragCounter.current.clear();

			if (!draggedId || draggedId === targetId) {
				setDragOverId(null);
				setDropPosition(null);
				return;
			}

			setTodos((prev) => {
				const newTodos = [...prev];
				const draggedIndex = newTodos.findIndex((t) => t.id === draggedId);
				const targetIndex = newTodos.findIndex((t) => t.id === targetId);
				if (draggedIndex === -1 || targetIndex === -1) return prev;

				const [dragged] = newTodos.splice(draggedIndex, 1);
				const insertIndex =
					dropPosition === "above" ? targetIndex : targetIndex + 1;
				// Adjust if we removed from before the target
				const adjustedIndex =
					draggedIndex < targetIndex ? insertIndex - 1 : insertIndex;
				newTodos.splice(adjustedIndex, 0, dragged);
				return newTodos;
			});

			setDraggedId(null);
			setDragOverId(null);
			setDropPosition(null);
		},
		[draggedId, dropPosition]
	);

	const activeTodos = todos.filter((t) => !t.completed);
	const completedTodos = todos.filter((t) => t.completed);
	const completedCount = completedTodos.length;
	const totalCount = todos.length;

	return (
		<div className="w-full max-w-lg mx-auto p-4 sm:p-8">
			<Card className="shadow-lg">
				<CardHeader className="pb-4">
					<CardTitle className="text-2xl font-bold text-center">
						Todo List
					</CardTitle>
					{totalCount > 0 && (
						<p className="text-sm text-muted-foreground text-center">
							{completedCount} of {totalCount} completed
						</p>
					)}
				</CardHeader>
				<CardContent className="space-y-4">
					{/* Add todo form */}
					<form
						onSubmit={(e) => {
							e.preventDefault();
							addTodo();
						}}
						className="flex gap-2"
					>
						<Input
							value={newTodo}
							onChange={(e) => setNewTodo(e.target.value)}
							placeholder="Add a new task..."
							className="flex-1"
						/>
						<Button type="submit" size="icon" disabled={!newTodo.trim()}>
							<Plus className="h-4 w-4" />
						</Button>
					</form>

					{/* Todo list */}
					{todos.length === 0 ? (
						<div className="py-12 text-center text-muted-foreground">
							<p className="text-lg">No tasks yet</p>
							<p className="text-sm mt-1">Add a task above to get started</p>
						</div>
					) : (
						<div className="space-y-1">
							{/* Active todos */}
							{activeTodos.map((todo) => (
								<TodoItem
									key={todo.id}
									todo={todo}
									isDragged={draggedId === todo.id}
									isDragOver={dragOverId === todo.id}
									dropPosition={
										dragOverId === todo.id ? dropPosition : null
									}
									onToggle={toggleTodo}
									onDelete={deleteTodo}
									onDragStart={handleDragStart}
									onDragEnd={handleDragEnd}
									onDragEnter={handleDragEnter}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
									onDrop={handleDrop}
								/>
							))}

							{/* Completed section */}
							{completedTodos.length > 0 && activeTodos.length > 0 && (
								<div className="pt-3 pb-1">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
										Completed
									</p>
								</div>
							)}
							{completedTodos.map((todo) => (
								<TodoItem
									key={todo.id}
									todo={todo}
									isDragged={false}
									isDragOver={false}
									dropPosition={null}
									onToggle={toggleTodo}
									onDelete={deleteTodo}
									onDragStart={handleDragStart}
									onDragEnd={handleDragEnd}
									onDragEnter={handleDragEnter}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
									onDrop={handleDrop}
								/>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

interface TodoItemProps {
	todo: Todo;
	isDragged: boolean;
	isDragOver: boolean;
	dropPosition: "above" | "below" | null;
	onToggle: (id: string) => void;
	onDelete: (id: string) => void;
	onDragStart: (e: React.DragEvent, id: string) => void;
	onDragEnd: (e: React.DragEvent) => void;
	onDragEnter: (e: React.DragEvent, id: string) => void;
	onDragOver: (e: React.DragEvent, id: string) => void;
	onDragLeave: (e: React.DragEvent, id: string) => void;
	onDrop: (e: React.DragEvent, id: string) => void;
}

function TodoItem({
	todo,
	isDragged,
	isDragOver,
	dropPosition,
	onToggle,
	onDelete,
	onDragStart,
	onDragEnd,
	onDragEnter,
	onDragOver,
	onDragLeave,
	onDrop,
}: TodoItemProps) {
	const isDraggable = !todo.completed;

	return (
		<div
			className={`
				relative flex items-center gap-2 rounded-lg border px-3 py-2.5
				transition-colors group
				${isDragged ? "opacity-50" : ""}
				${isDragOver && dropPosition === "above" ? "border-t-2 border-t-primary" : ""}
				${isDragOver && dropPosition === "below" ? "border-b-2 border-b-primary" : ""}
				${!isDragOver ? "border-border" : ""}
				${todo.completed ? "bg-muted/40" : "bg-card hover:bg-accent/50"}
			`}
			draggable={isDraggable}
			onDragStart={(e) => isDraggable && onDragStart(e, todo.id)}
			onDragEnd={onDragEnd}
			onDragEnter={(e) => onDragEnter(e, todo.id)}
			onDragOver={(e) => onDragOver(e, todo.id)}
			onDragLeave={(e) => onDragLeave(e, todo.id)}
			onDrop={(e) => onDrop(e, todo.id)}
		>
			{/* Drag handle */}
			{isDraggable && (
				<GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab active:cursor-grabbing shrink-0" />
			)}
			{!isDraggable && <div className="w-4 shrink-0" />}

			{/* Checkbox */}
			<Checkbox
				checked={todo.completed}
				onCheckedChange={() => onToggle(todo.id)}
				className="shrink-0"
			/>

			{/* Text */}
			<span
				className={`flex-1 text-sm select-none ${
					todo.completed
						? "line-through text-muted-foreground"
						: "text-foreground"
				}`}
			>
				{todo.text}
			</span>

			{/* Delete button */}
			<Button
				variant="ghost"
				size="icon"
				className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-muted-foreground hover:text-destructive"
				onClick={() => onDelete(todo.id)}
			>
				<Trash2 className="h-3.5 w-3.5" />
			</Button>
		</div>
	);
}

export default App;
